import { LevelDesignerElementBase } from "../elements/element-base";
import { LevelDesignerComponent } from "../level-designer/level-designer.component";
import { ToolGroup } from "./tool-group";
import { MouseEventResult } from "../managers/manager-base";

export enum ToolType {
    Toggle,    // Can be toggled on/off independently (like measurements)
    Action,    // Only one can be active at a time (like clone)
    Button,    // Instant, momentary action on click (like undo) - never stays pressed
}

export abstract class Tool {
    public boundary: { x: number; y: number; width: number; height: number } = { x: 0, y: 0, width: 0, height: 0 };
    public backgroundColor: string = '#FFFFFF'; 
    protected enabled: boolean = false;
    protected icon: HTMLImageElement | null = null;
    protected svgContent: string | null = null;

    constructor(
        protected canvas: HTMLCanvasElement, 
        protected levelDesigner: LevelDesignerComponent,
        protected toolGroup: ToolGroup,
        protected toolType: ToolType = ToolType.Toggle,
        protected iconPath?: string
    ) {
        if (iconPath) {
            this.loadIcon(iconPath);
        }
    }

    private loadIcon(path: string): void {
        if (path.toLowerCase().endsWith('.svg')) {
            // Load SVG file content
            fetch(path)
                .then(response => response.text())
                .then(svgContent => {
                    this.svgContent = svgContent;
                    this.levelDesigner.redraw();
                })
                .catch(error => {
                    console.error('Error loading SVG:', error);
                });
        } else {
            // Load regular image
            this.icon = new Image();
            this.icon.src = path;
        }
    }

    // Method to get the icon path
    public getIconPath(): string | undefined {
        return this.iconPath;
    }

    public abstract getName(): string;

    public activate(): void {
        this.enabled = true;
        this.onActivated();
        this.levelDesigner.redraw();
    }

    public deactivate(): void {
        this.enabled = false;
        this.onDeactivated();
        this.levelDesigner.redraw();
    }

    // Method to check if tool is currently active
    public isActive(): boolean {
        return this.enabled;
    }

    abstract onKeyPress(key: string): void;

    public onSelectionChanged(selectedElements: LevelDesignerElementBase[]): void {
        // Default implementation does nothing
    }

    protected getCanvasContext(): CanvasRenderingContext2D | null {
        return this.canvas.getContext('2d');
    }

    // Add this method to specify the button width
    public getButtonWidth(): number {
        return 50; // Default width
    }

    // Add this method to check if a point is inside the tool's boundary
    public isPointInside(x: number, y: number): boolean {
        return x >= this.boundary.x && x <= this.boundary.x + this.boundary.width &&
               y >= this.boundary.y && y <= this.boundary.y + this.boundary.height;
    }

    // Add method to get tooltip text
    public getTooltip(): string {
        return this.constructor.name; // Default to class name, override in specific tools
    }

    public getToolType(): ToolType {
        return this.toolType;
    }

    // Return true if the event was handled
    public onMouseMove(x: number, y: number): MouseEventResult | null {
        return null; // Default implementation doesn't handle events
    }

    public onMouseDown(x: number, y: number): MouseEventResult | null {
        return null;
    }

    public onMouseUp(x: number, y: number): MouseEventResult | null {
        return null;
    }

    public render(ctx: CanvasRenderingContext2D): void {
        // Default implementation does nothing
    }

    public onActivated(){
        // Default implementation does nothing
    }

    public onDeactivated(){
        // Default implementation does nothing
    }

    protected drawIcon(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
        if (this.icon) {
            ctx.drawImage(this.icon, x, y, width, height);
        }
    }
}
